import { KoreLocaleProvider } from "@pej/kore/intl";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { invalidateAuditLog } from "@/api/audit";
import { reauthOnQueryError, redirectToSso, shouldReauth } from "@/api/reauth";
import { shouldRetry } from "@/api/retry";
import { env } from "@/lib/env";
import { RootErrorComponent } from "./routes/__root";
import { routeTree } from "./routeTree.gen";
import "./app.css";

const handleReauth = (error: unknown) => {
    if (shouldReauth(error)) redirectToSso();
};

const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError: reauthOnQueryError }),
    mutationCache: new MutationCache({ onError: handleReauth, onSuccess: invalidateAuditLog }),
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            retry: shouldRetry,
            // Admin tool with long-lived tabs: fresh data on tab focus beats
            // silently stale data; staleTime debounces rapid tab flicks.
            refetchOnWindowFocus: true,
        },
    },
});

const router = createRouter({
    routeTree,
    context: { queryClient },
    defaultPreloadStaleTime: 30_000,
    defaultErrorComponent: RootErrorComponent,
});

declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}

function EnvConfigMissing() {
    return (
        <div style={{ padding: "4rem", fontFamily: "system-ui, sans-serif" }}>
            <h1>Configuration missing</h1>
            <p>This deployment did not receive its runtime configuration (env-config.js). Check the init container.</p>
        </div>
    );
}

const rootEl = document.getElementById("root");
if (rootEl) {
    const root = createRoot(rootEl);
    if (env.configMissing) {
        root.render(<EnvConfigMissing />);
    } else {
        root.render(
            <StrictMode>
                <QueryClientProvider client={queryClient}>
                    <KoreLocaleProvider>
                        <RouterProvider router={router} />
                    </KoreLocaleProvider>
                </QueryClientProvider>
            </StrictMode>,
        );
    }
}
